const LEVELS = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
  trace: 4
};

const DEFAULT_LEVEL = 'info';
const STORAGE_KEY = 'clientLogLevels';

const LOG_CATEGORIES = {
  GENERAL: 'general',
  CLIENT: 'client',
  NETWORK: 'network',
  HUD: 'hud',
  RENDERER: 'renderer',
  INPUT: 'input',
  AUDIO: 'audio',
  ABILITY: 'ability',
  PICKUP: 'pickup',
  LOOT: 'loot',
  WORLD: 'world',
  DEV: 'dev'
};

let levelConfig = loadStoredLevels();

applyQueryOverrides();

function loadStoredLevels() {
  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (_) {
    // ignore storage errors
  }
  return {};
}

function persistLevels() {
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(levelConfig));
  } catch (_) {
    // ignore storage errors
  }
}

function getLevelRank(level) {
  return LEVELS[level] ?? LEVELS[DEFAULT_LEVEL];
}

function getCategoryLevel(category) {
  return levelConfig[category] || levelConfig.default || DEFAULT_LEVEL;
}

function shouldLog(category, level) {
  const configuredLevel = getCategoryLevel(category);
  return getLevelRank(level) <= getLevelRank(configuredLevel);
}

function formatCategory(category) {
  return `[${category.toUpperCase()}]`;
}

function log(category, level, ...args) {
  const normalizedCategory = (category || LOG_CATEGORIES.GENERAL).toLowerCase();
  if (!shouldLog(normalizedCategory, level)) {
    return;
  }

  const consoleMethod = console[level] ? console[level] : console.log;
  consoleMethod.call(console, formatCategory(normalizedCategory), ...args);
}

function getLogger(category) {
  const normalizedCategory = (category || LOG_CATEGORIES.GENERAL).toLowerCase();
  return {
    error: (...args) => log(normalizedCategory, 'error', ...args),
    warn: (...args) => log(normalizedCategory, 'warn', ...args),
    info: (...args) => log(normalizedCategory, 'info', ...args),
    debug: (...args) => log(normalizedCategory, 'debug', ...args),
    trace: (...args) => log(normalizedCategory, 'trace', ...args)
  };
}

function setLevels(overrides) {
  if (!overrides || typeof overrides !== 'object') {
    return;
  }
  levelConfig = {
    ...levelConfig,
    ...overrides
  };
  persistLevels();
}

function getLevels() {
  return { ...levelConfig };
}

function parseLevelOverrides(input) {
  if (!input || typeof input !== 'string') {
    return {};
  }
  return input.split(',').reduce((acc, chunk) => {
    const [catRaw, levelRaw] = chunk.split(':').map(part => part && part.trim());
    if (!catRaw || !levelRaw) {
      return acc;
    }
    const level = levelRaw.toLowerCase();
    if (!LEVELS.hasOwnProperty(level)) {
      return acc;
    }
    acc[catRaw.toLowerCase()] = level;
    return acc;
  }, {});
}

function applyQueryOverrides() {
  try {
    const params = new URLSearchParams(window.location.search);
    const logParam = params.get('log') || params.get('logs');
    if (logParam) {
      const overrides = parseLevelOverrides(logParam);
      if (Object.keys(overrides).length > 0) {
        setLevels(overrides);
      }
    }
  } catch (_) {
    // ignore URL parsing errors
  }
}

const ClientLogger = {
  getLogger,
  setLevels,
  getLevels,
  categories: LOG_CATEGORIES,
  parseLevelOverrides,
  log
};

export { LEVELS, DEFAULT_LEVEL, STORAGE_KEY, LOG_CATEGORIES, levelConfig, loadStoredLevels, persistLevels, getLevelRank, getCategoryLevel, shouldLog, formatCategory, log, getLogger, setLevels, getLevels, parseLevelOverrides, applyQueryOverrides, ClientLogger };
